import React, { useState, useEffect } from 'react';
import { Expense, ExpenseCategory, CreateExpenseRequest, UpdateExpenseRequest } from '../types';
import DateInput from './DateInput';

interface ExpenseFormProps {
    expense?: Expense | null;
    categories: ExpenseCategory[];
    onSubmit: (data: CreateExpenseRequest | UpdateExpenseRequest) => void;
    onCancel: () => void;
}

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const parseToNumber = (raw: string) => {
    const onlyDigits = raw.replace(/\D/g, '');
    if (!onlyDigits) return 0;
    return parseInt(onlyDigits, 10) / 100;
};

const ExpenseForm: React.FC<ExpenseFormProps> = ({ expense, categories, onSubmit, onCancel }) => {
    const [categoryId, setCategoryId] = useState('');
    const [amountInput, setAmountInput] = useState('');
    const [dateIso, setDateIso] = useState<string | null>(null);
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (expense) {
            setCategoryId(expense.categoryId);
            setAmountInput(formatBRL((expense.amount || 0) / 100));
            setDateIso(expense.date);
            setDescription(expense.description || '');
        } else {
            const today = new Date();
            const utc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0));
            setDateIso(utc.toISOString());
            setCategoryId(categories[0]?.id ?? '');
            setAmountInput('');
            setDescription('');
        }
    }, [expense, categories]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const cents = Math.round(parseToNumber(amountInput) * 100);
        if (!categoryId || cents <= 0 || !dateIso) return;
        const dateStr = dateIso.split('T')[0];
        if (expense) {
            onSubmit({
                categoryId,
                amount: cents,
                date: dateStr,
                description: description.trim(),
            });
        } else {
            onSubmit({
                categoryId,
                amount: cents,
                date: dateStr,
                description: description.trim() || undefined,
            });
        }
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const numberValue = parseToNumber(raw);
        setAmountInput(formatBRL(numberValue));
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label htmlFor="categoryId" className="block text-sm font-medium text-secondary-700 mb-1">
                    Categoria *
                </label>
                <select
                    id="categoryId"
                    className="input-field"
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    required
                    disabled={categories.length === 0}
                >
                    {categories.length === 0 ? (
                        <option value="">Nenhuma categoria cadastrada</option>
                    ) : (
                        categories.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))
                    )}
                </select>
            </div>
            <div>
                <label htmlFor="amount" className="block text-sm font-medium text-secondary-700 mb-1">
                    Valor *
                </label>
                <input
                    type="text"
                    inputMode="numeric"
                    id="amount"
                    value={amountInput}
                    onChange={handleAmountChange}
                    required
                    className="input-field"
                    placeholder="R$ 0,00"
                />
            </div>
            <div>
                <DateInput
                    label="Data do gasto *"
                    name="date"
                    value={dateIso}
                    onChange={(v) => setDateIso(v)}
                    required
                />
            </div>
            <div>
                <label htmlFor="description" className="block text-sm font-medium text-secondary-700 mb-1">
                    Descrição
                </label>
                <input
                    type="text"
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={255}
                    className="input-field"
                    placeholder="Opcional"
                />
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
                <button type="button" onClick={onCancel} className="btn-secondary w-full sm:w-auto">
                    Cancelar
                </button>
                <button type="submit" className="btn-primary w-full sm:w-auto" disabled={categories.length === 0}>
                    {expense ? 'Salvar' : 'Registrar'}
                </button>
            </div>
        </form>
    );
};

export default ExpenseForm;
